import type { Wegstation } from '@epasu/core';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Der Weg der Firma, von der ersten Antwort bis zum laufenden Projekt.
 *
 * Gezeichnet wird, was der Dienst liefert (`organizations.weg`) — Länge und
 * Beschriftung hängen an der Zielgruppe. Die Leiste kennt keine Stationen von
 * sich aus; sonst zeigte sie einem Fotografen einen Vertrag und ein Projekt,
 * die es dort nie gibt.
 *
 * Erledigte Stationen sind grün, die aktuelle ist hervorgehoben und trägt
 * `aria-current`. Der Zustand steht zusätzlich als Text für Screenreader da,
 * damit er nicht allein an der Farbe hängt.
 */
export function Wegleiste({ stationen }: { stationen: Wegstation[] }) {
  if (stationen.length === 0) return null;

  return (
    <ol aria-label="Weg der Firma" className="flex flex-wrap items-center gap-2 text-xs">
      {stationen.map((s, i) => (
        <li
          key={`${i}-${s.label}`}
          aria-current={s.aktuell ? 'step' : undefined}
        >
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full border px-2.5 py-1',
              s.aktuell
                ? 'border-success bg-success font-semibold text-white shadow-sm'
                : s.erledigt
                  ? 'border-success/30 bg-success/15 text-success'
                  : 'border-border bg-card text-muted-foreground',
            )}
          >
            {s.erledigt && <Check aria-hidden className="h-3 w-3" />}
            {s.label}
            <span className="sr-only">
              {s.aktuell ? ' (hier steht die Firma)' : s.erledigt ? ' (erledigt)' : ' (offen)'}
            </span>
          </span>
        </li>
      ))}
    </ol>
  );
}
